import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CircleCheck, FileUp, TriangleAlert, Upload } from 'lucide-react';
import {
  AmRechnerBearbeiten,
  Button,
  Callout,
  Card,
  DataTable,
  DropZone,
  StatTile,
  cn,
  formatDatum,
  formatEuro,
  toast,
  useMobil,
  type Column,
  Schritte,
} from '@elbklar/ui';

import { SeitenKopf } from '@/components/layout/SeitenKopf';
import { SelectField } from '@/components/Felder';
import { importsApi } from '@/lib/api';
import type { ColumnMapping, ImportCommitResult, ImportPreview } from '@/types';

type Schritt = 'datei' | 'zuordnung' | 'fertig';

const SCHRITTE = [
  ['datei', 'Datei'],
  ['zuordnung', 'Zuordnung'],
  ['fertig', 'Übernahme'],
] as const;

/** Felder, die der Nutzer zuordnen kann. */
const FELDER: { key: keyof ColumnMapping; label: string; hinweis?: string }[] = [
  { key: 'booking_date', label: 'Buchungstag', hinweis: 'Pflicht' },
  { key: 'amount', label: 'Betrag', hinweis: 'oder Soll/Haben' },
  { key: 'debit', label: 'Soll', hinweis: 'wird zum Abfluss' },
  { key: 'credit', label: 'Haben', hinweis: 'wird zum Zufluss' },
  { key: 'counterparty', label: 'Empfänger' },
  { key: 'purpose', label: 'Verwendungszweck' },
  { key: 'balance', label: 'Saldo', hinweis: 'setzt den Kontostand' },
];

type VorschauZeile = ImportPreview['preview_parsed'][number] & { nr: number };

const VORSCHAU_SPALTEN: Column<VorschauZeile>[] = [
  { key: 'booking_date', header: 'Datum', mono: true, mobil: 'unter', render: (z) => formatDatum(z.booking_date) },
  {
    key: 'counterparty',
    header: 'Empfänger',
    strong: true,
    mobil: 'titel',
    render: (z) => <span className="block max-w-[200px] truncate">{z.counterparty ?? '–'}</span>,
  },
  {
    key: 'purpose',
    header: 'Zweck',
    render: (z) => <span className="block max-w-[240px] truncate text-muted-foreground">{z.purpose ?? '–'}</span>,
  },
  {
    key: 'amount',
    header: 'Betrag',
    align: 'end',
    mono: true,
    mobil: 'wert',
    render: (z) => (
      <span className={cn('font-medium', Number(z.amount) < 0 ? 'text-foreground' : 'text-success')}>
        {formatEuro(z.amount, { cent: true })}
      </span>
    ),
  },
];

/**
 * Kontoauszug importieren — Ist-Daten (U3), der Handweg neben der Übernahme
 * aus der Buchhaltung. Schlicht und am Rechner; am Handy nur die bisherigen
 * Importe.
 */
export function ImportPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const mobil = useMobil();
  const dateiFeld = useRef<HTMLInputElement>(null);

  const [schritt, setSchritt] = useState<Schritt>('datei');
  const [datei, setDatei] = useState<File | null>(null);
  const [vorschau, setVorschau] = useState<ImportPreview | null>(null);
  const [zuordnung, setZuordnung] = useState<Record<string, string>>({});
  const [ergebnis, setErgebnis] = useState<ImportCommitResult | null>(null);

  const historie = useQuery({
    queryKey: ['imports'],
    queryFn: importsApi.history,
  });

  const analysieren = useMutation({
    mutationFn: (f: File) => importsApi.preview(f),
    onSuccess: (daten) => {
      setVorschau(daten);
      // Das Mapping des letzten Laufs schlaegt den Rateversuch — in aller
      // Regel kommt der Auszug von derselben Bank.
      const start = daten.last_mapping ?? daten.suggested_mapping;
      const uebernommen: Record<string, string> = {};
      for (const [feld, spalte] of Object.entries(start)) {
        if (spalte && daten.header.includes(spalte)) uebernommen[feld] = spalte;
      }
      setZuordnung(uebernommen);
      setSchritt('zuordnung');
    },
    onError: () => toast.error('Datei konnte nicht gelesen werden — ist es ein CSV-Export?'),
  });

  const uebernehmen = useMutation({
    mutationFn: () => {
      if (!datei) throw new Error('keine Datei');
      const mapping = {
        booking_date: zuordnung.booking_date,
        amount: zuordnung.amount || null,
        debit: zuordnung.debit || null,
        credit: zuordnung.credit || null,
        counterparty: zuordnung.counterparty || null,
        purpose: zuordnung.purpose || null,
        balance: zuordnung.balance || null,
      } as ColumnMapping;
      return importsApi.commit(datei, mapping);
    },
    onSuccess: (daten) => {
      setErgebnis(daten);
      setSchritt('fertig');
      void queryClient.invalidateQueries({ queryKey: ['transactions'] });
      void queryClient.invalidateQueries({ queryKey: ['overview'] });
      void queryClient.invalidateQueries({ queryKey: ['imports'] });
      void queryClient.invalidateQueries({ queryKey: ['projection'] });
    },
    onError: () => toast.error('Übernahme fehlgeschlagen'),
  });

  const zuruecksetzen = () => {
    setSchritt('datei');
    setDatei(null);
    setVorschau(null);
    setZuordnung({});
    setErgebnis(null);
    if (dateiFeld.current) dateiFeld.current.value = '';
  };

  // Während eine Datei analysiert wird, nimmt die Fläche keine zweite an:
  // Sonst stünde die Vorschau von Datei A neben Datei B, und übernommen
  // würde B nach der Zuordnung von A. Die Fläche behält ihren Handler, damit
  // der Browser eine abgelegte Datei nicht selbst öffnet.
  const dateiGewaehlt = (f: File | null | undefined) => {
    if (!f || analysieren.isPending) return;
    setDatei(f);
    analysieren.mutate(f);
  };

  const kannUebernehmen =
    Boolean(zuordnung.booking_date) && Boolean(zuordnung.amount || zuordnung.debit || zuordnung.credit);

  const spaltenOptionen = [
    { value: '', label: 'nicht zugeordnet' },
    ...(vorschau?.header ?? []).map((spalte) => ({ value: spalte, label: spalte })),
  ];

  return (
    <div className="space-y-6">
      <SeitenKopf
        title="Kontoauszug importieren"
        subtitle="CSV hochladen, Spalten bestätigen, übernehmen. Bereits vorhandene Buchungen werden übersprungen — überlappende Auszüge sind also unproblematisch."
      />

      <AmRechnerBearbeiten className="md:hidden">Kontoauszüge importieren geht am Rechner.</AmRechnerBearbeiten>

      {!mobil && (
        <>
          <Schritte schritte={SCHRITTE.map(([, name]) => name)} aktuell={SCHRITTE.findIndex(([wert]) => wert === schritt)} />

          {schritt === 'datei' && (
            <>
              <input
                ref={dateiFeld}
                type="file"
                accept=".csv,text/csv,text/plain"
                className="hidden"
                onChange={(e) => dateiGewaehlt(e.target.files?.[0])}
              />
              <DropZone
                icon={FileUp}
                title="CSV-Datei hierher ziehen oder auswählen"
                description={
                  analysieren.isPending
                    ? 'Die Datei wird gelesen …'
                    : 'Der Export der Bank, üblicherweise semikolongetrennt. Umlaute und deutsche Beträge werden automatisch erkannt.'
                }
                active={analysieren.isPending || undefined}
                onFiles={(dateien) => dateiGewaehlt(dateien[0])}
                action={
                  <Button variant="primary" icon={Upload} loading={analysieren.isPending} onClick={() => dateiFeld.current?.click()}>
                    Datei wählen…
                  </Button>
                }
              />
            </>
          )}

          {schritt === 'zuordnung' && vorschau && (
            <>
              <Card
                title="Spalten zuordnen"
                description={`${vorschau.filename} · ${vorschau.total_rows} Zeilen · ${vorschau.encoding} · Trenner „${vorschau.delimiter}“${
                  vorschau.skipped_prefix_lines > 0 ? ` · ${vorschau.skipped_prefix_lines} Vorspannzeilen übersprungen` : ''
                }`}
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {FELDER.map(({ key, label, hinweis }) => (
                    <SelectField
                      key={key}
                      label={label}
                      hint={hinweis}
                      options={spaltenOptionen}
                      value={zuordnung[key] ?? ''}
                      onChange={(e) => setZuordnung((v) => ({ ...v, [key]: e.target.value }))}
                    />
                  ))}
                </div>

                {!kannUebernehmen && (
                  <Callout tone="warning" icon={TriangleAlert} title="Zuordnung unvollständig" boxed className="mt-4">
                    Mindestens Buchungstag und entweder Betrag oder Soll/Haben müssen zugeordnet sein.
                  </Callout>
                )}
              </Card>

              <section className="space-y-3">
                <div>
                  <h2 className="t-title">Vorschau der ersten Zeilen</h2>
                  <p className="t-small text-muted-foreground">So liest das System die Datei. Stimmen Datum und Betrag?</p>
                </div>

                {vorschau.preview_errors.length > 0 && (
                  <p className="flex items-start gap-2 text-warning">
                    <TriangleAlert size={16} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0" />
                    <span>
                      {vorschau.preview_errors.length}{' '}
                      {vorschau.preview_errors.length === 1 ? 'Zeile ließ' : 'Zeilen ließen'} sich nicht lesen — sie werden
                      beim Import übersprungen, der Rest läuft durch.
                    </span>
                  </p>
                )}

                <DataTable
                  columns={VORSCHAU_SPALTEN}
                  rows={vorschau.preview_parsed.slice(0, 8).map((z, nr) => ({ ...z, nr }))}
                  rowKey={(z) => String(z.nr)}
                  label="Vorschau"
                />
              </section>

              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={zuruecksetzen}>
                  Abbrechen
                </Button>
                <Button
                  variant="primary"
                  disabled={!kannUebernehmen}
                  loading={uebernehmen.isPending}
                  onClick={() => uebernehmen.mutate()}
                >
                  {vorschau.total_rows} Zeilen übernehmen
                </Button>
              </div>
            </>
          )}

          {schritt === 'fertig' && ergebnis && (
            <Card icon={CircleCheck} title="Import abgeschlossen">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <StatTile label="Übernommen" value={<span className="text-success">{ergebnis.inserted}</span>} />
                <StatTile label="Als Duplikat übersprungen" value={ergebnis.skipped_duplicates} />
                <StatTile label="Automatisch kategorisiert" value={ergebnis.auto_categorized} />
                <StatTile
                  label="Ohne Kategorie"
                  value={
                    ergebnis.uncategorized > 0 ? <span className="text-warning">{ergebnis.uncategorized}</span> : ergebnis.uncategorized
                  }
                />
              </div>

              {ergebnis.balance_snapshot_created && (
                <p className="mt-4 text-muted-foreground">Der Kontostand wurde aus der Saldo-Spalte übernommen.</p>
              )}

              {ergebnis.failed_rows > 0 && (
                <Callout
                  tone="warning"
                  icon={TriangleAlert}
                  title={`${ergebnis.failed_rows} ${ergebnis.failed_rows === 1 ? 'Zeile' : 'Zeilen'} übersprungen`}
                  boxed
                  className="mt-4"
                >
                  <ul className="list-inside list-disc t-small">
                    {ergebnis.errors.slice(0, 5).map((fehler, i) => (
                      <li key={i}>{fehler}</li>
                    ))}
                  </ul>
                </Callout>
              )}

              <div className="mt-5 flex flex-wrap gap-2">
                <Button variant="primary" onClick={zuruecksetzen}>
                  Weitere Datei importieren
                </Button>
                {ergebnis.uncategorized > 0 && (
                  <Button onClick={() => navigate('/buchungen?uncategorized=1')}>Offene Buchungen zuordnen</Button>
                )}
              </div>
            </Card>
          )}
        </>
      )}

      <Card title="Bisherige Importe">
        {!historie.data || historie.data.length === 0 ? (
          <p className="text-muted-foreground">Importierte Kontoauszüge erscheinen hier.</p>
        ) : (
          <ul className="divide-y divide-border">
            {historie.data.map((lauf) => (
              <li key={lauf.id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-2">
                <div className="min-w-0">
                  <div className="truncate font-medium">{lauf.filename}</div>
                  <div className="t-small text-muted-foreground">
                    {formatDatum(lauf.imported_at)} · {lauf.inserted_count} übernommen · {lauf.skipped_count} übersprungen
                  </div>
                </div>
                {lauf.error_message && <span className="t-small text-warning">{lauf.error_message}</span>}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
